// Errores de la ficha de Nexo (etapa 4): Nexo lee la base directo (public.nexo_viz_hallazgos y compañía), sin el servicio de Argus
// Team. La base responde con un código y un mensaje técnico (`P0002 hallazgos_corrida_cambio`); aquí se traduce a la misma razón que
// usa el servicio (app/nexo-investigator/src/nexo-rpc.ts, FICHA_ERRORS y los códigos sueltos, y server.ts) para que la pantalla diga el
// mismo texto que Argus Team (textos 46 a 52 del diseño 4a, en app/shared/rotulos/errores.ts desde el corte 4c). Sin React ni api.ts, para
// probarlo con Deno (app/scripts/nexo-ficha.test.ts).

/** La razón de un error de la ficha; la pantalla la convierte en texto con el catálogo compartido (MENSAJES_FICHA). */
export type RazonFicha =
  | "run_changed"
  | "ficha_no_encontrada"
  | "contraparte_no_encontrada"
  | "evidencia_corrida_cambio"
  | "evidencia_no_encontrada"
  | "ficha_sin_frase"
  | "invalid_request"
  | "nexo_timeout"
  | "respuesta_muy_grande"
  | "unauthorized"
  | "network"
  | "server_error";

/**
 * Código y mensaje de la base → razón, como FICHA_ERRORS del servicio: un mensaje no listado no se interpreta. Copia de esa tabla de
 * plomería (no es una regla de lo que se afirma); la prueba de Nexo la compara con la del servicio.
 */
export const ERRORES_DE_LA_BASE: Record<string, RazonFicha> = {
  "P0002 hallazgos_corrida_cambio": "run_changed",
  "P0003 hallazgos_subject_not_found": "ficha_no_encontrada",
  "P0003 hallazgos_contraparte_no_encontrada": "contraparte_no_encontrada",
  "P0002 evidencia_corrida_cambio": "evidencia_corrida_cambio",
  "P0003 evidencia_no_encontrada": "evidencia_no_encontrada",
  "P0005 hallazgos_frase_sin_variante": "ficha_sin_frase",
};

/**
 * Código solo → razón, como el servicio (nexo-rpc.ts: 54000 es `response_limit`, que server.ts vuelve `respuesta_muy_grande`; 22023 es
 * `nexo_invalid_arguments`, que server.ts vuelve `invalid_request`; 57014 es `nexo_timeout`). La prueba lo compara con el servicio.
 */
export const ERRORES_POR_CODIGO: Record<string, RazonFicha> = {
  "22023": "invalid_request",
  "57014": "nexo_timeout",
  "54000": "respuesta_muy_grande",
};

/** Lo que la pantalla sabe de un error: el mensaje, el estado HTTP y el código de la base (la forma de ApiError de api.ts). */
export interface ErrorLeido {
  message: string;
  status?: number | undefined;
  codigo?: string | null | undefined;
}

/** Un error de la API de Nexo, reconocido por su forma (sin importar api.ts, que lee import.meta.env): `name` «ApiError». */
export function leerError(e: unknown): ErrorLeido | null {
  if (typeof e !== "object" || e === null) return null;
  const x = e as { name?: unknown; message?: unknown; status?: unknown; codigo?: unknown };
  if (x.name !== "ApiError" || typeof x.message !== "string") return null;
  return {
    message: x.message,
    status: typeof x.status === "number" ? x.status : undefined,
    codigo: typeof x.codigo === "string" ? x.codigo : null,
  };
}

/** La razón de un error de nexo_viz_hallazgos, nexo_viz_hallazgo_detalle o nexo_viz_evidencia. */
export function razonDeError(e: ErrorLeido): RazonFicha {
  const codigo = e.codigo ?? "";
  const conocida = ERRORES_DE_LA_BASE[`${codigo} ${e.message}`] ?? ERRORES_POR_CODIGO[codigo];
  if (conocida) return conocida;
  // 401: la sesión venció y renovarla no bastó (api.ts ya lleva a la pantalla de entrada).
  if (e.status === 401) return "unauthorized";
  // 408: el cliente se cansó de esperar (api.ts, REQUEST_TIMEOUT_MS).
  if (e.status === 408) return "nexo_timeout";
  if (e.status === undefined || e.status === 0) return "network";
  return "server_error";
}

/** La razón de cualquier cosa lanzada al leer la ficha, una relación o una fuente. */
export function razonDe(e: unknown): RazonFicha {
  const leido = leerError(e);
  return leido ? razonDeError(leido) : "network";
}

/** El texto para la persona: siempre el del catálogo (`textos`, MENSAJES_FICHA), nunca el mensaje crudo de la base. */
export function mensajeDeRazon(razon: RazonFicha, textos: (codigo: string) => string): string {
  return textos(razon);
}

/** ¿La razón pide buscar de nuevo (la corrida cambió o la persona ya no está), en lugar de reintentar? */
export const pideBuscarDeNuevo = (r: string): boolean =>
  r === "run_changed" || r === "ficha_no_encontrada" || r === "contraparte_no_encontrada" || r === "evidencia_corrida_cambio";

/** Qué botón lleva un error: «Buscar de nuevo», «Reintentar» o ninguno (respuesta demasiado grande, parte inexistente, sesión vencida). */
export function accionDeError(r: string): "buscar" | "reintentar" | "ninguna" {
  if (pideBuscarDeNuevo(r)) return "buscar";
  if (r === "respuesta_muy_grande" || r === "evidencia_no_encontrada" || r === "unauthorized") return "ninguna";
  return "reintentar";
}
